import React, { forwardRef, memo, useRef, useCallback } from 'react';
import type { BaseTextAreaProps } from './TextArea.types';
import { createSyntheticChangeEvent } from './TextArea.types';
import './TextArea.css';
import { useAutoGrow } from './useAutoGrow';
import { TextAreaButtons } from './TextAreaButtons';

const TextAreaComponent = forwardRef<HTMLTextAreaElement, BaseTextAreaProps>(
  (
    {
      placeholder = '@mentions or comment',
      value = '',
      onChange,
      onKeyDown,
      onSubmit,
      className = '',
      style,
      autoGrow = true,
      minRows = 1,
      maxRows = 5,
      showSubmitButton = false,
      showClearButton = false,
      submitButtonText = 'Submit',
      clearButtonText = '×',
      children,
      ...props
    },
    ref
  ) => {
    const textareaRef = useRef<HTMLTextAreaElement>(null);

    // Forward ref to actual textarea
    React.useImperativeHandle(ref, () => textareaRef.current!, []);

    const { currentHeight } = useAutoGrow(textareaRef, value, {
      autoGrow,
      minRows,
      maxRows
    });

    const handleKeyDown = useCallback(
      (event: React.KeyboardEvent<HTMLTextAreaElement>) => {
        // Call parent onKeyDown FIRST (this should handle mentions)
        onKeyDown?.(event);

        // Check if event was handled by parent (mentions)
        if (event.defaultPrevented) {
          return;
        }

        // Submit on Enter (not Ctrl/Cmd + Enter)
        if (event.key === 'Enter' && !event.shiftKey && onSubmit) {
          event.preventDefault();
          onSubmit(value);
        }
      },
      [onKeyDown, onSubmit, value]
    );

    const handleChange = useCallback(
      (event: React.ChangeEvent<HTMLTextAreaElement>) => {
        onChange?.(event);
        // Height will be updated by the useEffect watching value changes
      },
      [onChange]
    );

    const handleSubmit = useCallback(() => {
      onSubmit?.(value);
    }, [onSubmit, value]);

    const handleClear = useCallback(() => {
      if (onChange && textareaRef.current) {
        const syntheticEvent = createSyntheticChangeEvent(
          textareaRef.current,
          ''
        );
        onChange(syntheticEvent);
      }
    }, [onChange]);

    const textareaClassName = `textarea-component__field${
      showSubmitButton || showClearButton || children
        ? ' textarea-component__field--with-buttons'
        : ''
    }${autoGrow ? ' textarea-component__field--auto-grow' : ''}`;

    return (
      <div className={`textarea-component ${className}`} style={style}>
        <div className="textarea-component__wrapper">
          <textarea
            ref={textareaRef}
            value={value}
            placeholder={placeholder}
            onChange={handleChange}
            onKeyDown={handleKeyDown}
            className={textareaClassName}
            rows={autoGrow ? 1 : minRows}
            spellCheck={false}
            style={{
              height: autoGrow ? currentHeight : undefined,
              resize: autoGrow ? 'none' : 'vertical'
            }}
            {...props}
          />

          <TextAreaButtons
            showClearButton={showClearButton}
            showSubmitButton={showSubmitButton}
            clearButtonText={clearButtonText}
            submitButtonText={submitButtonText}
            value={value}
            onClear={handleClear}
            onSubmit={handleSubmit}
          >
            {children}
          </TextAreaButtons>
        </div>
      </div>
    );
  }
);

TextAreaComponent.displayName = 'TextArea';

export const TextArea = memo(TextAreaComponent);

export default TextArea;
